import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../api";
import PacketFormView from "../onboarding/PacketFormView.jsx";
import { StatusPill } from "./AdminOnboarding.jsx";
import { readFileAsDataUrl, fmtTime, BRAND, GOLD } from "../onboarding/helpers.js";

function Card({ title, children, right }) {
  return (
    <section className="bg-white border rounded-xl shadow-sm">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-2"><h2 className="font-bold" style={{ color: BRAND }}>{title}</h2>{right}</div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function PacketDetail({ id }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");
  const [blockers, setBlockers] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const load = () => api.onboarding.hr.get(id).then(setD).catch((e) => setError(e.message));
  useEffect(() => { load(); }, [id]);

  const act = async (name, fn, ok) => {
    setBusy(name); setError(""); setNotice(""); setBlockers(null);
    try { const r = await fn(); if (ok) setNotice(typeof ok === "function" ? ok(r) : ok); await load(); }
    catch (e) { if (e.data?.missing) setBlockers(e.data.missing); else setError(e.message); }
    finally { setBusy(""); }
  };
  const upload = (kind) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    await act(`up-${kind}`, async () => api.onboarding.hr.upload(id, { kind, file_name: file.name, mime_type: file.type || "application/octet-stream", file_data: await readFileAsDataUrl(file) }), `${file.name} uploaded and filed in the employee's documents.`);
  };

  if (error && !d) return <div className="text-red-600">{error}</div>;
  if (!d) return <div className="text-gray-500">Loading…</div>;
  const p = d.packet;
  const certified = p.status === "certified";
  const byStage = d.forms.reduce((m, f) => ({ ...m, [f.stage]: [...(m[f.stage] || []), f] }), {});

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      <Link to="/admin/onboarding" className="text-sm hover:underline" style={{ color: BRAND }}>← New Hire Packets</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.15em]" style={{ color: GOLD }}>NEW HIRE PACKET #{p.id}</div>
          <h1 className="text-2xl font-bold" style={{ color: BRAND }}>{d.employee.name}</h1>
          <p className="text-sm text-gray-500">{p.position} · <Link className="underline" to={`/admin/employees/${d.employee.employee_id}`}>{d.employee.employee_id}</Link> · hire date {d.employee.hire_date || "—"}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2"><StatusPill status={p.status} /><span className="text-sm text-gray-600">{d.done}/{d.total} employee forms</span></div>
      </div>

      {!d.signer_adopted && <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">Adopt your HR signature before countersigning. <Link to="/admin/onboarding/signature" className="underline font-semibold">Adopt signature</Link></div>}
      {notice && <div className="text-sm bg-green-50 border border-green-200 text-green-800 rounded-lg p-3">{notice}</div>}
      {error && <div className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">{error}</div>}
      {blockers && (
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="font-semibold text-amber-900 mb-1">Not ready to certify — complete these first:</div>
          <ul className="list-disc ml-5 text-amber-900 max-h-64 overflow-auto">{blockers.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <Card title="Forms" right={<span className="text-xs text-gray-500">{d.hr_missing_total} HR item{d.hr_missing_total === 1 ? "" : "s"} outstanding</span>}>
            {[1, 2, 3, 0].filter((s) => byStage[s]).map((s) => (
              <div key={s} className="mb-4 last:mb-0">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{s === 0 ? "HR records" : `Step ${s} · ${d.stage_names[s]}`}{s === 2 && p.stage_open < 2 ? " · not yet opened to employee" : ""}</div>
                <ul className="divide-y border rounded-lg">
                  {byStage[s].map((f) => (
                    <li key={f.id}>
                      <Link to={`/admin/onboarding/${id}/forms/${f.id}`} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
                        <span className={`w-5 h-5 shrink-0 rounded-full text-[11px] flex items-center justify-center font-bold ${f.hr_only ? "bg-gray-200 text-gray-600" : f.emp_status === "signed" ? "bg-green-600 text-white" : "border-2 border-gray-300"}`}>{f.hr_only ? "HR" : f.emp_status === "signed" ? "✓" : ""}</span>
                        <span className="flex-1 min-w-0">
                          <span className="text-[11px] font-bold mr-2" style={{ color: GOLD }}>{f.num}</span>
                          <span className="text-sm">{f.title}</span>
                        </span>
                        {f.hr_missing.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 whitespace-nowrap">HR: {f.hr_missing.length}</span>}
                        {!f.hr_only && f.emp_status !== "signed" && f.emp_missing.length > 0 && <span className="text-[11px] text-gray-500 whitespace-nowrap hidden sm:inline">employee: {f.emp_missing.length}</span>}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>

          <Card title="Audit trail" right={<span className={`text-xs ${d.audit_chain.ok ? "text-green-700" : "text-red-700"}`}>{d.audit_chain.ok ? "✓ hash chain intact" : `✗ integrity check failed (#${d.audit_chain.bad_id})`}</span>}>
            <button onClick={() => setShowLog((v) => !v)} className="text-sm underline" style={{ color: BRAND }}>{showLog ? "Hide" : "Show"} {d.audit.length} recent events</button>
            {showLog && (
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-xs">
                  <thead className="text-left text-gray-500"><tr><th className="py-1 pr-2">When</th><th className="pr-2">Actor</th><th className="pr-2">Event</th><th className="pr-2">Detail</th><th>IP</th></tr></thead>
                  <tbody className="divide-y">{d.audit.map((a) => <tr key={a.id}><td className="py-1 pr-2 whitespace-nowrap">{fmtTime(a.at)}</td><td className="pr-2">{a.actor}</td><td className="pr-2">{a.event}</td><td className="pr-2">{a.detail}</td><td>{a.ip}</td></tr>)}</tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-5">
          <Card title="Next steps">
            <ol className="text-sm space-y-3">
              <li className="flex gap-2"><span className="font-bold" style={{ color: GOLD }}>1</span><span>Countersign the offer letter — <Link className="underline" to={`/admin/onboarding/${id}/forms/f2`}>open Form 02</Link>. {d.forms.find((f) => f.id === "f2")?.hr_missing.length === 0 ? <b className="text-green-700">Done</b> : null}</span></li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: GOLD }}>2</span><span>Upload the NH DOS criminal record and BEAS registry results below.</span></li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: GOLD }}>3</span>
                <span>After orientation, open the training forms (19–21) to the employee.
                  {p.stage_open < 2 ? <button disabled={busy === "stage2"} onClick={() => act("stage2", () => api.onboarding.hr.openOrientation(id), "Orientation forms opened.")} className="block mt-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-60" style={{ backgroundColor: BRAND }}>Open orientation forms</button> : <b className="text-green-700"> Opened</b>}
                </span>
              </li>
              <li className="flex gap-2"><span className="font-bold" style={{ color: GOLD }}>4</span>
                <span>Certify the file. This locks it and files the final signed PDF in the employee's documents.
                  {!certified ? <button disabled={busy === "certify"} onClick={() => { if (window.confirm("Certify this personnel file complete and clear the employee for client contact? The file will be locked.")) act("certify", () => api.onboarding.hr.certify(id), (r) => r.pdf_error || "Certified. The final signed PDF is in Signed PDFs."); }} className="block mt-1 px-3 py-1.5 rounded-lg text-white text-xs font-semibold bg-green-700 disabled:opacity-60">{busy === "certify" ? "Certifying…" : "Certify & clear for client contact"}</button> : <b className="text-green-700"> Certified {fmtTime(p.certified_at)}</b>}
                </span>
              </li>
            </ol>
          </Card>

          <Card title="HR uploads">
            <ul className="space-y-2">
              {d.hr_uploads.map((u) => (
                <li key={u.kind} className="flex items-center justify-between gap-2 text-sm">
                  <span>{u.on_file ? "✓ " : ""}{u.label}{u.required && !u.on_file && <span className="text-red-600"> *</span>}</span>
                  <label className="text-xs font-semibold cursor-pointer whitespace-nowrap" style={{ color: BRAND }}>
                    {busy === `up-${u.kind}` ? "Uploading…" : u.on_file ? "Add another" : "Upload"}
                    <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload(u.kind)} />
                  </label>
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-gray-500 mt-2">* required before certification. Files are saved to the employee's document vault.</p>
            {d.files.length > 0 && (
              <div className="mt-3 border-t pt-2">
                <div className="text-xs font-semibold text-gray-500 mb-1">All attachments</div>
                <ul className="text-xs space-y-1">{d.files.map((f) => <li key={f.id}><a className="underline" href={api.onboarding.hr.fileUrl(id, f.id)} target="_blank" rel="noreferrer">{f.label}: {f.filename}</a> <span className="text-gray-500">· {f.uploaded_by === "employee" ? "employee" : "HR"}</span></li>)}</ul>
              </div>
            )}
          </Card>

          <Card title="Signed PDFs" right={<button disabled={busy === "pdf"} onClick={() => act("pdf", () => api.onboarding.hr.generatePdf(id), "Working copy generated.")} className="text-xs font-semibold disabled:opacity-60" style={{ color: BRAND }}>{busy === "pdf" ? "Generating…" : "Generate working copy"}</button>}>
            {d.documents.length === 0 ? <p className="text-sm text-gray-500">None yet. Signed copies of key forms are filed automatically as the employee finishes them.</p> : (
              <ul className="text-sm space-y-2">
                {d.documents.map((doc) => (
                  <li key={doc.id}>
                    <a className="underline" href={api.onboarding.hr.documentUrl(id, doc.id)} target="_blank" rel="noreferrer">{doc.label}</a>
                    <div className="text-[11px] text-gray-500 break-all">{fmtTime(doc.created_at)} · SHA-256 {doc.sha256}</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Annual attestation">
            <p className="text-sm text-gray-600">He-P 822.17(t) requires the attestation at hire and every year. The employee signs it in the portal.</p>
            {p.annual_year && <p className="text-sm mt-1">Currently open: <b>{p.annual_year}</b></p>}
            <button disabled={busy === "annual"} onClick={() => act("annual", () => api.onboarding.hr.openAnnual(id, d.year), `Annual attestation ${d.year} sent to the employee's portal.`)} className="mt-2 px-3 py-1.5 rounded-lg border text-xs font-semibold" style={{ borderColor: BRAND, color: BRAND }}>Request {d.year} attestation</button>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function AdminOnboardingPacket() {
  const { packetId, formId } = useParams();
  if (!formId) return <PacketDetail id={packetId} />;
  const io = {
    key: `${packetId}:${formId}`,
    load: () => api.onboarding.hr.form(packetId, formId),
    save: (v) => api.onboarding.hr.save(packetId, formId, v),
    stamp: (k) => api.onboarding.hr.stamp(packetId, formId, k),
    reopen: (reason) => api.onboarding.hr.reopen(packetId, formId, reason),
    fileUrl: (fid) => api.onboarding.hr.fileUrl(packetId, fid),
  };
  return <PacketFormView mode="hr" io={io} backTo={`/admin/onboarding/${packetId}`} />;
}
